import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { Box, Button, Flex, Progress, SimpleGrid, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { createErrorToast, createSuccessToast } from '../../../redux/modules/app';
import { useImportFiles, useUploadImportFiles, useUploadImportManifest } from '../../../services/import-service';
import colors from '../../../styles/theme/foundation/colors';

import ImportFilesTable from './ImportFilesTable';
import { acceptsUploads, hasManifest } from './util';

const MAX_FILE_SIZE = 50 * 1024 * 1024;

const Zone = ({ title, hint, onDrop, accept, multiple, isBusy, testId, directory = false }) => {
  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop,
    multiple,
    maxSize: MAX_FILE_SIZE,
    accept,
    disabled: isBusy,
    useFsAccessApi: false
  });

  // React does not reliably forward webkitdirectory as a prop, so the folder
  // picker is switched on at the DOM level once the input exists
  useEffect(() => {
    if (directory && inputRef.current) {
      inputRef.current.setAttribute('webkitdirectory', '');
      inputRef.current.setAttribute('directory', '');
    }
  }, [directory, inputRef]);

  // a folder picker cannot select loose files, so a directory zone gets a second,
  // click-only picker for individual files
  const filePicker = useDropzone({ onDrop, multiple: true, maxSize: MAX_FILE_SIZE, disabled: isBusy, noDrag: true, noClick: true, useFsAccessApi: false });

  return (
    <Flex
      {...getRootProps()}
      alignItems="center"
      backgroundColor="greyScale.100"
      border={`1px dashed ${colors.primaryScale['100']}`}
      borderRadius="lg"
      cursor={isBusy ? 'not-allowed' : 'pointer'}
      data-testid={testId}
      flexDir="column"
      justifyContent="center"
      minH="140px"
      opacity={isBusy ? 0.6 : 1}
      padding={4}
      textAlign="center"
    >
      <input {...getInputProps()} data-testid={`${testId}-input`} />
      <Text mb={1} textStyle="bodyLg500">{isDragActive ? 'Drop here…' : title}</Text>
      <Text color="greyScale.500" textStyle="bodySm">{hint}</Text>
      {directory && (
        <>
          <input {...filePicker.getInputProps()} data-testid={`${testId}-files-input`} />
          <Button
            data-testid={`${testId}-pick-files`}
            isDisabled={isBusy}
            mt={2}
            size="xs"
            variant="textGrey"
            onClick={event => { event.stopPropagation(); filePicker.open(); }}
          >
            or choose individual files
          </Button>
        </>
      )}
    </Flex>
  );
};

Zone.propTypes = {
  title: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
  onDrop: PropTypes.func.isRequired,
  accept: PropTypes.object,
  multiple: PropTypes.bool,
  isBusy: PropTypes.bool,
  testId: PropTypes.string,
  directory: PropTypes.bool
};

/**
 * Browser upload of the manifest (presigned PUT) and the files (presigned POST
 * per file, sequential). Dropping a folder keeps each file's relative path,
 * which is what the manifest's file_path column refers to.
 */
const ImportUploads = ({ importData, showManifest = true, showFiles = true }) => {
  const dispatch = useDispatch();
  const [progress, setProgress] = useState(null);

  const { data: files } = useImportFiles(importData.id);
  const { mutateAsync: uploadManifest, isLoading: manifestUploading } = useUploadImportManifest(importData.id);
  const { mutateAsync: uploadFiles, isLoading: filesUploading } = useUploadImportFiles(importData.id);

  const enabled = acceptsUploads(importData);
  const busy = manifestUploading || filesUploading;
  const stagedCount = files?.total ?? 0;

  const onManifestDrop = useCallback(async accepted => {
    if (!accepted.length) {
      return;
    }

    try {
      await uploadManifest(accepted[0]);
      dispatch(createSuccessToast({ title: 'Manifest uploaded', description: 'Validate to check it against the staged files.' }));
    } catch (error) {
      dispatch(createErrorToast({ title: 'Manifest upload failed', description: error?.message, error }));
    }
  }, [uploadManifest]);

  const onFilesDrop = useCallback(async accepted => {
    if (!accepted.length) {
      return;
    }

    setProgress({ done: 0, total: accepted.length, failures: [] });

    try {
      const result = await uploadFiles({ files: accepted, onProgress: setProgress });

      if (result.failures.length) {
        dispatch(createErrorToast({
          title: `${result.failures.length} of ${accepted.length} file(s) failed to upload`,
          description: result.failures.slice(0, 3).map(failure => `${failure.path}: ${failure.message}`).join(' · ')
        }));
      } else {
        dispatch(createSuccessToast({ title: `${result.uploaded} file(s) staged` }));
      }
    } catch (error) {
      dispatch(createErrorToast({ title: 'Upload failed', description: error?.message, error }));
    } finally {
      setProgress(null);
    }
  }, [uploadFiles]);

  return (
    <Box>
      <SimpleGrid columns={showManifest && showFiles ? 2 : 1} spacing={4}>
        {showManifest && (
          <Zone
            accept={{ 'text/csv': ['.csv'], 'text/plain': ['.csv'] }}
            hint={hasManifest(importData) ? 'A manifest is staged; drop a new one to replace it' : 'Drop manifest.csv or browse'}
            isBusy={!enabled || busy}
            multiple={false}
            testId="manifest-dropzone"
            title={hasManifest(importData) ? 'Replace the manifest' : 'Upload the manifest'}
            onDrop={onManifestDrop}
          />
        )}
        {showFiles && (
          <Zone
            directory
            multiple
            hint={`${files?.total ?? 0} file(s) staged · click to pick the folder, or drop it here · max 50 MB each`}
            isBusy={!enabled || busy}
            testId="files-dropzone"
            title="Upload the files"
            onDrop={onFilesDrop}
          />
        )}
      </SimpleGrid>

      {progress && (
        <Box mt={3}>
          <Progress hasStripe isAnimated borderRadius="md" value={progress.total ? Math.round((progress.done / progress.total) * 100) : 0} />
          <Text mt={1} textStyle="bodySm">{`Uploading ${progress.done} of ${progress.total}${progress.failures.length ? ` · ${progress.failures.length} failed` : ''}`}</Text>
        </Box>
      )}

      {!enabled && (
        <Text color="greyScale.500" mt={2} textStyle="bodySm">Uploads are closed while the import is being processed.</Text>
      )}

      {showFiles && <ImportFilesTable canEdit={enabled && !busy} importId={importData.id} stagedTotal={stagedCount} />}
    </Box>
  );
};

ImportUploads.propTypes = {
  importData: PropTypes.object.isRequired,
  showManifest: PropTypes.bool,
  showFiles: PropTypes.bool
};

export default ImportUploads;
